<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import AccountScheduledMessagesAPI from '../../../../api/accountScheduledMessages';
import ConversationApi from '../../../../api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';
import ScheduledMessagesCalendarView from './ScheduledMessagesCalendarView.vue';

const emit = defineEmits(['switchView']);
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const loading = ref(true);
const scheduledMessages = ref([]);
const expandedIds = ref([]);
const statusFilter = ref('all');
const inboxFilter = ref('all');
const recurrenceFilter = ref('all');
const showDeleteModal = ref(false);
const messageToDelete = ref(null);
const searchQuery = ref('');
const showSearchInput = ref(false);
const showScheduleModal = ref(false);
const viewMode = ref('list');
const calendarViewMode = ref('month');
const calendarTodayTrigger = ref(0);
const showInboxDropdown = ref(false);
const inboxFilterDropdownRef = ref(null);

const handleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (showSearchInput.value) {
    nextTick(() => document.getElementById('scheduled-messages-search')?.focus());
  }
};

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const accountId = computed(() => store.getters.getCurrentAccountId);
const selectedInbox = computed(() => {
  if (inboxFilter.value === 'all') return null;
  return inboxes.value.find(i => i.id === Number(inboxFilter.value)) || null;
});

const selectedInboxDisplayLabel = computed(() => {
  if (!selectedInbox.value) return t('SCHEDULE_MESSAGES.FILTER.ALL_CHANNELS');
  return selectedInbox.value.name;
});

const filteredMessages = computed(() => {
  let list = scheduledMessages.value;
  if (statusFilter.value !== 'all') {
    list = list.filter(m => m.status === statusFilter.value);
  }
  if (inboxFilter.value !== 'all') {
    list = list.filter(m => m.inbox_id === Number(inboxFilter.value));
  }
  if (recurrenceFilter.value !== 'all') {
    list = list.filter(m =>
      recurrenceFilter.value === 'recurrent'
        ? m?.is_recurrent === true
        : m?.is_recurrent !== true
    );
  }
  const q = searchQuery.value?.trim().toLowerCase();
  if (!q) return list;
  return list.filter(m => {
    const title = (m.title || '').toLowerCase();
    const message = (m.message || '').toLowerCase();
    const contact = ((m.contact_name || '') + (m.contact_email || '')).toLowerCase();
    const inbox = getInboxName(m.inbox_id).toLowerCase();
    return title.includes(q) || message.includes(q) || contact.includes(q) || inbox.includes(q);
  });
});

const fetchMessages = async () => {
  try {
    loading.value = true;
    const res = await AccountScheduledMessagesAPI.get();
    scheduledMessages.value = res.data?.payload || [];
  } catch (err) {
    useAlert(t('SCHEDULE_MESSAGES.ERROR.LOAD'), 'error');
  } finally {
    loading.value = false;
  }
};

const formatDate = ts => {
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInboxName = inboxId => {
  const inbox = inboxes.value.find(i => i.id === inboxId);
  return inbox ? inbox.name : t('SCHEDULE_MESSAGES.ERROR.INBOX_NOT_FOUND');
};

const getContactDisplayName = msg => {
  const name = msg?.contact_name;
  const email = msg?.contact_email;
  const fallback = t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
  const text = name || email || fallback;
  return text.length > 25 ? `${text.substring(0, 25)}...` : text;
};

const getStatusText = status => {
  return status === 'sent'
    ? t('SCHEDULE_MESSAGES.STATUS.SENT')
    : t('SCHEDULE_MESSAGES.STATUS.PENDING');
};

const getStatusClass = status => ({
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300': status === 'pending',
  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': status === 'sent',
});

const getPeriodText = period => {
  const type = typeof period === 'object' && period !== null ? period.type : period;
  const map = {
    every_day: 'EVERY_DAY',
    every_day_at_time: 'EVERY_DAY_AT_TIME',
    every_week: 'EVERY_WEEK',
    every_month: 'EVERY_MONTH',
    every_weekday: 'EVERY_WEEKDAY',
    every_weekend: 'EVERY_WEEKEND',
  };
  return t(`SCHEDULE_MESSAGES.FREQUENCY.${map[type] || 'EVERY_DAY'}`);
};

const toggleExpand = id => {
  const i = expandedIds.value.indexOf(id);
  if (i === -1) expandedIds.value.push(id);
  else expandedIds.value.splice(i, 1);
};

const openDeleteModal = msg => {
  messageToDelete.value = msg;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  messageToDelete.value = null;
};

const closeScheduleModal = () => {
  showScheduleModal.value = false;
  fetchMessages();
};

const confirmDelete = async () => {
  if (!messageToDelete.value?.conversation_display_id) return;
  try {
    await ConversationApi.deleteScheduledMessage(
      messageToDelete.value.conversation_display_id,
      messageToDelete.value.id
    );
    useAlert(t('SCHEDULE_MESSAGES.SUCCESS.DELETE'), 'success');
    await fetchMessages();
  } catch (err) {
    useAlert(t('SCHEDULE_MESSAGES.ERROR.DELETE'), 'error');
  } finally {
    closeDeleteModal();
  }
};

const goToConversation = msg => {
  if (!msg?.conversation_display_id || !accountId.value) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      inboxId: msg.inbox_id,
      conversation_id: msg.conversation_display_id,
    },
  });
};

const toggleInboxDropdown = () => {
  showInboxDropdown.value = !showInboxDropdown.value;
};

const selectInbox = value => {
  inboxFilter.value = value;
  showInboxDropdown.value = false;
};

const triggerCalendarToday = () => {
  calendarTodayTrigger.value += 1;
};

const handleOutsideClick = event => {
  if (!showInboxDropdown.value) return;
  if (!inboxFilterDropdownRef.value?.contains(event.target)) {
    showInboxDropdown.value = false;
  }
};

onMounted(() => {
  fetchMessages();
  document.addEventListener('click', handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white dark:bg-slate-900 overflow-hidden">
    <!-- Header: mesma estrutura que KanbanHeader (flex justify-between, tudo na mesma linha) -->
    <div
      class="relative z-10 flex flex-nowrap justify-between items-center pt-2 px-4 md:pt-3 md:px-6 md:gap-4 shrink-0"
    >
      <div class="flex items-center gap-1 min-w-0 md:gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-chevron-left cursor-pointer text-slate-600 dark:text-slate-400 shrink-0"
          @click="emit('switchView', 'kanban')"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        <h2 class="text-base font-medium text-slate-900 dark:text-slate-100 truncate">
          {{ t('SCHEDULE_MESSAGES.LIST.TITLE') }}
        </h2>
        <div
          class="flex items-center relative gap-1"
          :class="{ 'is-active': showSearchInput }"
        >
          <Button variant="ghost" color="slate" size="sm" @click="handleSearch">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.3-4.3"/>
              </svg>
            </template>
          </Button>
          <div
            v-show="showSearchInput || searchQuery"
            class="h-8 flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 overflow-hidden transition-all duration-200 min-w-[200px] focus-within:border-woot-500 focus-within:ring-1 focus-within:ring-woot-500"
          >
            <input
              id="scheduled-messages-search"
              v-model="searchQuery"
              type="search"
              class="w-full h-full bg-transparent border-none outline-none ring-0 m-0 px-3 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 placeholder:text-xs"
              :placeholder="t('SCHEDULE_MESSAGES.SEARCH_PLACEHOLDER')"
              @blur="!searchQuery && (showSearchInput = false)"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-1 md:gap-2 flex-nowrap shrink-0">
        <Button
          v-if="viewMode === 'calendar'"
          variant="ghost"
          size="sm"
          color="slate"
          class="px-3 py-1.5"
          @click="triggerCalendarToday"
        >
          {{ t('KANBAN.AGENDA.VIEW_MODES.TODAY') }}
        </Button>
        <div
          v-if="viewMode === 'calendar'"
          class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
        >
          <Button
            variant="ghost"
            size="sm"
            class="px-3 py-1.5 border-r border-slate-200 dark:border-slate-700 rounded-none"
            :color="calendarViewMode === 'month' ? 'blue' : 'slate'"
            :class="{
              'bg-slate-100 dark:bg-slate-800 text-woot-600 dark:text-woot-400':
                calendarViewMode === 'month',
              'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50':
                calendarViewMode !== 'month',
            }"
            @click="calendarViewMode = 'month'"
          >
            {{ t('KANBAN.AGENDA.VIEW_MODES.MONTH') }}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="px-3 py-1.5 rounded-none"
            :color="calendarViewMode === 'week' ? 'blue' : 'slate'"
            :class="{
              'bg-slate-100 dark:bg-slate-800 text-woot-600 dark:text-woot-400':
                calendarViewMode === 'week',
              'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50':
                calendarViewMode !== 'week',
            }"
            @click="calendarViewMode = 'week'"
          >
            {{ t('KANBAN.AGENDA.VIEW_MODES.WEEK') }}
          </Button>
        </div>
        <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <Button
            variant="ghost"
            size="sm"
            color="slate"
            class="rounded-none border-r border-slate-200 dark:border-slate-700"
            :class="{
              'bg-slate-100 dark:bg-slate-800 text-woot-600 dark:text-woot-400': viewMode === 'list',
            }"
            @click="viewMode = 'list'"
          >
            <span class="i-lucide-list size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            color="slate"
            class="rounded-none"
            :class="{
              'bg-slate-100 dark:bg-slate-800 text-woot-600 dark:text-woot-400': viewMode === 'calendar',
            }"
            @click="viewMode = 'calendar'"
          >
            <span class="i-lucide-calendar-days size-4" />
          </Button>
        </div>
        <div class="flex items-center gap-2">
          <div ref="inboxFilterDropdownRef" class="relative">
            <button
              type="button"
              class="h-8 m-0 min-w-[180px] px-3 py-1.5 text-[12px] border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer opacity-50 hover:opacity-100 hover:border-slate-400 dark:hover:border-slate-500 transition-all duration-200 flex items-center justify-between gap-2"
              :class="{ 'ring-2 ring-blue-500 border-transparent': showInboxDropdown }"
              @click="toggleInboxDropdown"
            >
              <span class="flex items-center gap-2 min-w-0">
                <ChannelIcon
                  v-if="selectedInbox"
                  :inbox="selectedInbox"
                  class="size-4 flex-shrink-0 text-slate-600 dark:text-slate-300"
                />
                <span v-else class="i-lucide-inbox size-4 flex-shrink-0 text-slate-600 dark:text-slate-300" />
                <span class="truncate">{{ selectedInboxDisplayLabel }}</span>
              </span>
              <span class="i-lucide-chevron-down size-3.5 flex-shrink-0 text-black/70 dark:text-slate-400" />
            </button>
            <div
              v-if="showInboxDropdown"
              class="absolute top-full left-0 mt-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-50 min-w-[180px] max-h-56 overflow-y-auto"
            >
              <button
                type="button"
                class="w-full text-left px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer flex items-center gap-2"
                @click="selectInbox('all')"
              >
                <span class="i-lucide-inbox size-4 flex-shrink-0 text-slate-600 dark:text-slate-300" />
                <span>{{ t('SCHEDULE_MESSAGES.FILTER.ALL_CHANNELS') }}</span>
              </button>
              <button
                v-for="inbox in inboxes"
                :key="inbox.id"
                type="button"
                class="w-full text-left px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer flex items-center gap-2"
                :class="{ 'bg-slate-100 dark:bg-slate-600': Number(inboxFilter) === inbox.id }"
                @click="selectInbox(inbox.id)"
              >
                <ChannelIcon
                  :inbox="inbox"
                  class="size-[14px] flex-shrink-0 text-slate-600 dark:text-slate-300"
                />
                <span class="truncate">{{ inbox.name }}</span>
              </button>
            </div>
          </div>
          <div class="relative">
            <select
              v-model="statusFilter"
              class="h-8 m-0 min-w-[120px] appearance-none bg-none px-3 pr-8 py-1.5 text-[12px] border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer opacity-50 hover:opacity-100 hover:border-slate-400 dark:hover:border-slate-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{{ t('SCHEDULE_MESSAGES.FILTER.ALL') }}</option>
              <option value="pending">{{ t('SCHEDULE_MESSAGES.FILTER.PENDING') }}</option>
              <option value="sent">{{ t('SCHEDULE_MESSAGES.FILTER.SENT') }}</option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-black/70 dark:text-slate-400"
            />
          </div>
          <div class="relative">
            <select
              v-model="recurrenceFilter"
              class="h-8 m-0 min-w-[140px] appearance-none bg-none px-3 pr-8 py-1.5 text-[12px] border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer opacity-50 hover:opacity-100 hover:border-slate-400 dark:hover:border-slate-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{{ t('SCHEDULE_MESSAGES.FILTER.MESSAGE_TYPE_ALL') }}</option>
              <option value="recurrent">{{ t('SCHEDULE_MESSAGES.FILTER.MESSAGE_TYPE_RECURRENT') }}</option>
              <option value="single">{{ t('SCHEDULE_MESSAGES.FILTER.MESSAGE_TYPE_SINGLE') }}</option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-black/70 dark:text-slate-400"
            />
          </div>
        </div>
        <div class="flex items-center">
          <Button
            type="button"
            variant="solid"
            color="blue"
            size="sm"
            class="shrink-0 whitespace-nowrap"
            @click="showScheduleModal = true"
          >
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 2v4"/>
                <path d="M16 2v4"/>
                <rect width="18" height="18" x="3" y="4" rx="2"/>
                <path d="M3 10h18"/>
                <path d="M8 14h.01"/>
                <path d="M12 14h.01"/>
                <path d="M16 14h.01"/>
                <path d="M8 18h.01"/>
                <path d="M12 18h.01"/>
                <path d="M16 18h.01"/>
              </svg>
            </template>
            <span class="text-[12px] whitespace-nowrap">{{ t('SCHEDULE_MESSAGES.BUTTON_LABEL') }}</span>
          </Button>
        </div>
      </div>
    </div>

    <ScheduleMessageModal
      :show="showScheduleModal"
      @close="closeScheduleModal"
    />

    <div v-if="loading" class="flex-1 flex items-center justify-center p-8">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-10" />
    </div>

    <div
      v-else-if="filteredMessages.length === 0"
      class="flex-1 flex flex-col items-center justify-center p-8 text-center"
    >
      <span class="i-lucide-calendar-clock size-12 text-n-blue-10 mb-4" />
      <p class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ t('SCHEDULE_MESSAGES.EMPTY') }}
      </p>
      <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
        {{ t('SCHEDULE_MESSAGES.EMPTY_SUBTEXT') }}
      </p>
    </div>

    <ScheduledMessagesCalendarView
      v-else-if="viewMode === 'calendar'"
      :messages="filteredMessages"
      :calendar-view-mode="calendarViewMode"
      :today-trigger="calendarTodayTrigger"
      @open-conversation="goToConversation"
      @delete-message="openDeleteModal"
    />

    <div v-else class="flex-1 overflow-y-auto p-4 space-y-3">
      <div
        v-for="msg in filteredMessages"
        :key="msg.id"
        class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
      >
        <button
          type="button"
          class="w-full flex items-start justify-between gap-2 p-4 text-left hover:bg-n-solid-3 transition-colors"
          @click="toggleExpand(msg.id)"
        >
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium text-slate-900 dark:text-slate-100 truncate">
                {{ msg.title || t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
              </span>
              <span
                class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                :class="getStatusClass(msg.status)"
              >
                {{ getStatusText(msg.status) }}
              </span>
            </div>
            <div class="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-calendar size-3.5" />
                {{ formatDate(msg.scheduled_at) }}
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-inbox size-3.5" />
                {{ getInboxName(msg.inbox_id) }}
              </span>
              <span
                v-if="msg.is_recurrent"
                class="inline-flex items-center gap-1"
              >
                <span class="i-lucide-repeat size-3.5" />
                {{ getPeriodText(msg.period) }}
              </span>
            </div>
            <!-- Bloco conversa de origem (estilo KanbanItem: display_id + nome contato + inbox) -->
            <div
              v-if="msg.conversation_display_id != null"
              class="flex items-center gap-2 mt-2 px-3.5 py-1.5 rounded-md border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs min-h-[1.5rem] w-full max-w-full cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/80"
              style="box-shadow: 0 4px 24px 0 rgba(30,41,59,0.08)"
              @click.stop="goToConversation(msg)"
            >
              <div class="flex items-center gap-1 shrink-0">
                <span class="i-lucide-message-circle size-3 text-slate-400 dark:text-slate-400" />
                <span class="text-[10px] text-slate-400 dark:text-slate-400 select-all px-0.5">
                  #{{ msg.conversation_display_id }}
                </span>
              </div>
              <div class="flex items-center gap-1 min-w-0 flex-1">
                <span class="truncate font-semibold text-slate-700 dark:text-slate-200">
                  {{ getContactDisplayName(msg) }}
                </span>
                <span class="text-slate-300 dark:text-slate-600 shrink-0">·</span>
                <span class="truncate text-slate-500 dark:text-slate-400 shrink-0 max-w-[120px]">
                  {{ getInboxName(msg.inbox_id) }}
                </span>
              </div>
            </div>
          </div>
          <span
            class="shrink-0 size-5 text-slate-400"
            :class="expandedIds.includes(msg.id) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>
        <div
          v-show="expandedIds.includes(msg.id)"
          class="border-t border-n-weak bg-n-solid-1 p-4 space-y-3"
        >
          <div class="rounded-lg bg-n-solid-2 p-3">
            <p class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {{ t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
            </p>
            <p class="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
              {{ msg.message }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              v-if="msg.conversation_display_id"
              variant="ghost"
              color="slate"
              size="sm"
              @click="goToConversation(msg)"
            >
              <span class="i-lucide-message-circle size-4 mr-1" />
              {{ t('SCHEDULE_MESSAGES.LIST.OPEN_CONVERSATION') }}
            </Button>
            <Button
              variant="ghost"
              color="ruby"
              size="sm"
              @click="openDeleteModal(msg)"
            >
              <span class="i-lucide-trash-2 size-4 mr-1" />
              {{ t('SCHEDULE_MESSAGES.LIST.DELETE') }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        @click.self="closeDeleteModal"
      >
        <div
          class="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6 border border-n-weak"
          role="dialog"
          aria-modal="true"
        >
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.TITLE') }}
          </h3>
          <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.MESSAGE') }}
          </p>
          <div class="mt-6 flex justify-end gap-2">
            <Button variant="ghost" color="slate" size="sm" @click="closeDeleteModal">
              {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.KEEP') }}
            </Button>
            <Button variant="solid" color="ruby" size="sm" @click="confirmDelete">
              {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.CONFIRM') }}
            </Button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
